import { useEffect, useMemo, useRef } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";

const DEFAULT_CENTER = {
  lat: -16.409047,
  lng: -71.537451,
};

const COLORS = ["#2563eb", "#16a34a", "#f97316", "#dc2626", "#7c3aed"];
const SELECTED_ZONE_ZOOM_OFFSET = 3;

function getZonePolygon(zone) {
  return Array.isArray(zone?.poligone) ? zone.poligone : zone?.polygon || [];
}

function zoomOutAfterFitBounds(map) {
  window.setTimeout(() => {
    const zoom = map.getZoom();

    if (typeof zoom === "number") {
      map.setZoom(Math.max(zoom - SELECTED_ZONE_ZOOM_OFFSET, 1));
    }
  }, 0);
}

function ZonePolygons({ zones, selectedZoneId, onSelectZone }) {
  const map = useMap();
  const polygonsRef = useRef([]);

  const normalizedZones = useMemo(
    () =>
      zones
        .map((zone, index) => ({
          ...zone,
          mapColor: COLORS[index % COLORS.length],
          points: getZonePolygon(zone).filter(
            (point) =>
              typeof point.latitude === "number" &&
              typeof point.longitude === "number",
          ),
        }))
        .filter((zone) => zone.points.length >= 3),
    [zones],
  );

  useEffect(() => {
    if (!map || !window.google) return undefined;

    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    polygonsRef.current = [];

    const allBounds = new window.google.maps.LatLngBounds();
    const selectedBounds = new window.google.maps.LatLngBounds();

    normalizedZones.forEach((zone) => {
      const isSelected = zone.id === selectedZoneId;
      const path = zone.points.map((point) => {
        const position = {
          lat: point.latitude,
          lng: point.longitude,
        };

        allBounds.extend(position);

        if (isSelected) {
          selectedBounds.extend(position);
        }

        return position;
      });

      const polygon = new window.google.maps.Polygon({
        paths: path,
        strokeColor: zone.mapColor,
        strokeOpacity: 0.95,
        strokeWeight: isSelected ? 4 : 2,
        fillColor: zone.mapColor,
        fillOpacity: isSelected ? 0.28 : 0.16,
        clickable: true,
        map,
      });

      polygon.addListener("click", () => {
        onSelectZone?.(zone);
      });

      polygonsRef.current.push(polygon);
    });

    if (!selectedBounds.isEmpty()) {
      map.fitBounds(selectedBounds, 88);
      zoomOutAfterFitBounds(map);
    } else if (!allBounds.isEmpty()) {
      map.fitBounds(allBounds, 56);
    }

    return () => {
      polygonsRef.current.forEach((polygon) => polygon.setMap(null));
      polygonsRef.current = [];
    };
  }, [map, normalizedZones, onSelectZone, selectedZoneId]);

  return null;
}

export default function ZoneReadonlyMap({ zones, selectedZoneId, onSelectZone }) {
  return (
    <APIProvider
      apiKey={"AIzaSyBIbtiiqiEJ6F2ScRafjfv1SmpaeWg3NDQ"}
      libraries={["geometry", "places"]}
    >
      <Map
        style={{
          width: "100%",
          height: "100%",
        }}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={12}
        gestureHandling="greedy"
      >
        <ZonePolygons
          zones={zones}
          selectedZoneId={selectedZoneId}
          onSelectZone={onSelectZone}
        />
      </Map>
    </APIProvider>
  );
}
